import React, { useState, useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { useForm } from '@abyss/web/hooks/useForm';
import { Button } from '@abyss/web/ui/Button';
import { styled } from '@abyss/web/tools/styled';
import { SelectInput } from '@abyss/web/ui/SelectInput';
import { Alert } from '@abyss/web/ui/Alert';
import { PropsContext } from 'src/hooks/usePropsContext/PropsContext';
import { usePaanSpecialtyType } from 'src/hooks/usePaanSpecialtyType';
import { usePaanZipRadiusType } from 'src/hooks/usePaanZipRadiusType';
import { usePaanOrgType } from 'src/hooks/usePaanOrgType';
import {
  useProviderDataSearch,
  parseResult,
} from 'src/hooks/useProviderDataSearch';
import { useAdminContext } from 'src/hooks/useAdminContext';
import { AlertContext } from 'src/tools/AlertContext';
import { useUserInfoStore } from 'src/store/useUserInfoStore';
import { config } from '@abyss/ui/tools/config';
import { SearchResults } from './components/SearchResults';
import { NoMatchesFound } from './components/NoMatchesFound';
import { AlliedAncillaryForm } from './AlliedAncillaryForm';
import { FacilityForm } from './FacilityForm';
import { PhysicianForm } from './PhysicianForm';
import { ApiErrorModal } from './components/ApiErrorModal';

const PROVIDER_TYPE_MAP = {
  physician: '1',
  facility: '2',
  allied_ancillary: '3',
};

const SEARCH_TYPE_MAP = {
  npi: '1',
  name_state: '2',
  state_city: '3',
  tax_id_number: '4',
  zip_code: '5',
};

const ActionButtons = styled('div', {
  marginTop: 25,

  '> *:first-child': {
    marginRight: 12,
  },
});

const StyledSearchContainer = styled('div', {
  marginTop: 25,
});

const StyledContinueContainer = styled('div', {
  marginTop: 25,
});

export const FindForm = ({ adminContextRole }) => {
  const [isApiErrorModalOpen, setIsApiErrorModalOpen] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNoMatchesFound, setShowNoMatchesFound] = useState(false);
  const [formAlert, setFormAlert] = useState(false);
  const [disableContinueButton, setDisableContinueButton] = useState(true);
  const [selectedSearchData, setSelectedSearchData] = useState({});
  const serverErrorCodes = [500, 501, 502, 503, 504];
  const [typeStatus, setTypeStatus] = useState({
    radiusTypeFlag: {
      isError: false,
    },
    orgTypeFlag: {
      isError: false,
    },
    specialtyTypeFlag: {
      isError: false,
    },
  });
  const [isSpecialityFieldRequired, setIsSpecialityFieldRequired] = useState(
    false
  );
  const [
    paanSpecialtyTypesResponse,
    getPaanSpecialtyTypes,
  ] = usePaanSpecialtyType({
    onError: () => {
      setTypeStatus(prev => ({
        ...prev,
        specialtyTypeFlag: { isError: true },
      }));
    },
  });
  const [paanOrgTypesResponse, getPaanOrgTypes] = usePaanOrgType({
    onError: () => {
      setTypeStatus(prev => ({ ...prev, orgTypeFlag: { isError: true } }));
    },
  });
  const [paanZipRadiusResponse, getPaanZipRadiusTypes] = usePaanZipRadiusType({
    onError: () => {
      setTypeStatus(prev => ({ ...prev, radiusTypeFlag: { isError: true } }));
    },
  });

  const [searchAttributes, setSearchAttributes] = useState({});
  const { data: userInfo } = useUserInfoStore();
  const errorContext = useContext(AlertContext);
  const buttonRef = useRef();

  const [, getProviders] = useProviderDataSearch({
    onCompleted: response => {
      setShowSearchResults(true);
      setFormAlert(false);
      setShowNoMatchesFound(false);
    },
    onError: error => {
      if (serverErrorCodes.includes(error.status)) {
        setIsApiErrorModalOpen(true);
      } else {
        setShowNoMatchesFound(true);
      }
      setShowSearchResults(false);
      setFormAlert(false);
    },
  });

  const [, setAdminContext] = useAdminContext({
    onCompleted: () => {
      window.location.replace(config('PAAN_URL'));
    },
    onError: error => {
      errorContext.addAlert({
        ...error,
        message:
          'The system is unable to respond at the moment. Please try refreshing the page or try again at a later time',
      });
    },
  });

  const PROVIDERS_SEARCH_TYPE_OPTIONS = [
    { label: 'Allied-Ancillary', value: 'allied_ancillary' },
    { label: 'Facility', value: 'facility' },
    { label: 'Physician', value: 'physician' },
  ];
  const SEARCH_BY_OPTIONS = [
    { label: 'NPI', value: 'npi' },
    { label: 'Name/state', value: 'name_state' },
    { label: 'State/city', value: 'state_city' },
    { label: 'Tax ID number', value: 'tax_id_number' },
    { label: 'Zip code', value: 'zip_code' },
  ];

  const form = useForm({
    defaultValues: {
      searchType: 'physician',
      searchBy: 'npi',
    },
  });

  const searchType = form.watch('searchType');
  const searchBy = form.watch('searchBy');

  useEffect(() => {
    // get all the types!
    getPaanSpecialtyTypes();
    getPaanOrgTypes();
    getPaanZipRadiusTypes();
  }, []);

  useEffect(() => {
    if (searchBy === 'zip_code') {
      setIsSpecialityFieldRequired(true);
    } else {
      form.clearErrors('physician.specialtyCode');
      setIsSpecialityFieldRequired(false);
    }
    // Clear form fields for current search type when searchBy changes
    if (searchType) {
      form.setValue(searchType, {});
    }
  }, [searchBy]);

  const handleProviderSearchChange = () => {
    form.setValue('searchBy', 'npi');
  };

  const getPageData = async (page = 0, pageSize = 10) => {
    let recordNumber = 0;

    // API pageStart value is actually the record start value.
    // Example: 11 total results, sending pageStart of 10 will return the last result (11th result)
    if (page > 0) {
      recordNumber = page * pageSize;
    }
    const result = await getProviders({
      variables: {
        ...searchAttributes,
        pageStart: recordNumber.toString(),
        pageCount: pageSize,
      },
    });

    const data = result.body.data ? result.body.data : result.body;
    const parsedResult = parseResult(data);

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          results: parsedResult.providerData,
          count: parsedResult.total,
        });
      }, 1000);
    });
  };

  const handleSearch = data => {
    setShowNoMatchesFound(false);
    const searchParams = {
      searchType: PROVIDER_TYPE_MAP[data.searchType],
      searchBy: SEARCH_TYPE_MAP[data.searchBy],
      ...data[searchType],
    };

    if (data[searchType].name) {
      searchParams.lastName = data[searchType].name;
    }

    setSearchAttributes(searchParams);
    setShowSearchResults(true);
  };

  const handleFormError = () => {
    setFormAlert(true);
  };

  const handleReset = () => {
    form.reset({
      searchType,
      searchBy: 'npi',
    });
    setDisableContinueButton(true);
    setShowSearchResults(false);
    setShowNoMatchesFound(false);
    setFormAlert(false);
  };

  const handleOnBlur = field => {
    form.trigger(field);
  };

  const handleContinue = () => {
    const provider = {
      taxIdNumber: selectedSearchData.taxId,
      servicingProviderMpin: selectedSearchData.ndbMpin,
      ...(searchType === 'physician' && {
        servicingProviderFirstName: selectedSearchData.firstName,
      }),
      servicingProviderLastName:
        searchType === 'physician'
          ? selectedSearchData.lastName
          : selectedSearchData.providerName,
      phoneNumber: selectedSearchData.primaryPhone.slice(0, 12),
    };
    setAdminContext({
      variables: {
        role: adminContextRole,
        id: userInfo?.uuid,
        username: userInfo?.username,
        provider,
      },
    });
  };

  const widgetProps = {
    modelPrefix: searchType,
    searchBy,
    isSpecialityFieldRequired,
    onBlur: handleOnBlur,
    specialtyTypes: paanSpecialtyTypesResponse.data || [],
    organizationTypes: paanOrgTypesResponse.data || [],
    zipRadiusTypes: paanZipRadiusResponse.data || [],
    ...typeStatus,
    typesSubText:
      'You can refresh the page to try again or select a different "Search By" option.',
    typesErrorPlaceholder: 'Unable to load options',
  };

  const closeModal = () => {
    setIsApiErrorModalOpen(false);
  };

  return (
    <React.Fragment>
      <ApiErrorModal isOpen={isApiErrorModalOpen} onClose={closeModal} />
      <Alert
        css={{ marginBottom: 20, width: '50%' }}
        title="Please complete all required fields."
        isVisible={formAlert}
        onClose={() => {
          setFormAlert(false);
          if (buttonRef.current) {
            buttonRef.current.focus();
          }
        }}
      >
        Required fields are marked with an asterisk(*).
      </Alert>
      <FormProvider
        state={form}
        name="psa"
        onSubmit={handleSearch}
        onError={handleFormError}
      >
        <div>
          <SelectInput
            label="Provider search type"
            placeholder="Select Provider"
            model="searchType"
            isSearchable
            options={PROVIDERS_SEARCH_TYPE_OPTIONS}
            width="50%"
            validators={{ required: true }}
            autoFocus
            onChange={handleProviderSearchChange}
          />
          <SelectInput
            label="Search by"
            placeholder="Select"
            model="searchBy"
            isSearchable
            options={SEARCH_BY_OPTIONS}
            width="50%"
            validators={{ required: true }}
          />
          <PropsContext.Provider value={widgetProps}>
            <If condition={searchType === 'allied_ancillary'}>
              <AlliedAncillaryForm />
            </If>
            <If condition={searchType === 'facility'}>
              <FacilityForm />
            </If>
            <If condition={searchType === 'physician'}>
              <PhysicianForm />
            </If>
          </PropsContext.Provider>
        </div>
        <ActionButtons>
          <Button type="submit" ref={buttonRef}>
            Search
          </Button>
          <Button type="reset" onClick={handleReset} variant="outline">
            Reset
          </Button>
        </ActionButtons>
      </FormProvider>
      <If condition={showSearchResults}>
        <StyledSearchContainer>
          <SearchResults
            setDisableContinueButton={setDisableContinueButton}
            setSelectedSearchData={setSelectedSearchData}
            searchAttributes={searchAttributes}
            getPageData={getPageData}
          />
        </StyledSearchContainer>
      </If>
      <If condition={showNoMatchesFound}>
        <StyledSearchContainer>
          <NoMatchesFound />
        </StyledSearchContainer>
      </If>
      {showSearchResults && (
        <StyledContinueContainer>
          <Button isDisabled={disableContinueButton} onClick={handleContinue}>
            Continue
          </Button>
        </StyledContinueContainer>
      )}
    </React.Fragment>
  );
};

FindForm.propTypes = {
  adminContextRole: PropTypes.string.isRequired,
};
